<template>
  <div class="ncrm-contacts-adv-filter">
    <v-tooltip location="bottom">
      <template #activator="{ props }">
        <v-badge
          :model-value="hasActiveFilters"
          :content="activeConditionCount"
          color="primary"
          offset-x="4"
          offset-y="4"
          class="ncrm-contacts-adv-filter__trigger"
        >
          <v-btn
            v-bind="props"
            icon="mdi-filter-variant"
            :variant="hasActiveFilters ? 'tonal' : 'text'"
            color="primary"
            density="comfortable"
            :aria-label="$t('ncrm.contacts.advancedFilter.open')"
            @click="openBuilder"
          />
        </v-badge>
      </template>
      <span>{{ $t("ncrm.contacts.advancedFilter.open") }}</span>
    </v-tooltip>

    <NcrmRecordEditDialog
      v-model="dialogOpen"
      :title="$t('ncrm.contacts.advancedFilter.title')"
      :subtitle="$t('ncrm.contacts.advancedFilter.subtitle')"
      header-icon="mdi-filter-variant"
      :save-label="$t('ncrm.contacts.advancedFilter.apply')"
      :save-disabled="!draftHasValidCondition"
      :extra-action-label="$t('ncrm.contacts.advancedFilter.clearAll')"
      extra-action-color="error"
      :extra-action-disabled="!hasActiveFilters && !draftConditions.length"
      :max-width="920"
      @save="applyDraft"
      @extra-action="clearAllFromBuilder"
    >
      <div class="ncrm-contacts-adv-filter__toolbar mb-3">
        <v-select
          v-model="draftLogic"
          :label="$t('ncrm.contacts.advancedFilter.logic')"
          :items="logicOptions"
          item-title="label"
          item-value="value"
          :menu-props="selectMenuProps"
          hide-details
          class="ncrm-contacts-adv-filter__logic-select"
        />
        <v-btn size="small" color="primary" variant="tonal" prepend-icon="mdi-plus" @click="addConditionRow">
          {{ $t("ncrm.contacts.advancedFilter.addCondition") }}
        </v-btn>
      </div>

      <v-progress-linear v-if="fieldsLoading" indeterminate color="primary" class="mb-3" />

      <v-row
        v-for="(condition, index) in draftConditions"
        :key="condition._uid"
        class="ncrm-contacts-adv-filter__condition-row"
        dense
      >
        <v-col cols="12" md="4">
          <v-autocomplete
            v-model="condition.field_key"
            :label="$t('ncrm.contacts.advancedFilter.field')"
            :items="fieldOptions"
            item-title="title"
            item-value="key"
            :menu-props="selectMenuProps"
            hide-details
            clearable
            @update:model-value="onConditionFieldChanged(condition)"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="condition.operator"
            :label="$t('ncrm.contacts.advancedFilter.operator')"
            :items="operatorOptionsFor(condition)"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
            hide-details
            @update:model-value="onConditionOperatorChanged(condition)"
          />
        </v-col>
        <v-col v-if="operatorRequiresValue(condition.operator)" cols="12" md="4">
          <component
            :is="condition.field_key === 'privacy_state' ? 'v-select' : 'v-combobox'"
            v-model="condition.value"
            :label="$t('ncrm.contacts.advancedFilter.value')"
            :items="valueOptionsFor(condition)"
            item-title="title"
            item-value="value"
            :return-object="false"
            :loading="condition.value_loading"
            :menu-props="selectMenuProps"
            variant="outlined"
            density="comfortable"
            hide-details
            clearable
            @update:search="(search) => scheduleValueSearch(condition, search)"
            @focus="ensureValueSuggestions(condition)"
          />
        </v-col>
        <v-col class="d-flex align-center justify-end" cols="12" md="1">
          <v-btn
            icon="mdi-close"
            size="x-small"
            variant="text"
            color="error"
            :disabled="draftConditions.length <= 1"
            :aria-label="$t('ncrm.contacts.advancedFilter.removeCondition')"
            @click="removeConditionRow(index)"
          />
        </v-col>
      </v-row>

      <v-alert v-if="fieldsError" type="error" density="compact" class="mt-2">
        {{ fieldsError }}
      </v-alert>
    </NcrmRecordEditDialog>
  </div>
</template>

<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import { PRIVACY_STATUS_CODES } from "@/Utile/ncrmPrivacyStatus.mjs";
import {
  contactsFilterOperatorRequiresValue,
  normalizeContactsAdvancedFilters,
  CONTACTS_FILTER_OPERATOR_VALUES,
} from "@/Utile/ncrmContactsAdvancedFilter.mjs";

const VALUE_SEARCH_DEBOUNCE_MS = 260;
let conditionUidSeq = 0;
const nextConditionUid = () => `ncrm-contacts-filter-condition-${(conditionUidSeq += 1)}`;

export default {
  name: "NcrmContactsAdvancedFilter",
  components: { NcrmRecordEditDialog },
  props: {
    modelValue: {
      type: Object,
      default: () => ({ logic: "and", conditions: [] }),
    },
    type: {
      type: String,
      required: true,
      validator: (value) => value === "company" || value === "person",
    },
    open: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update:open"],
  data() {
    return {
      draftLogic: "and",
      draftConditions: [],
      fields: [],
      fieldsLoading: false,
      fieldsError: "",
      fieldsLoadedForType: "",
    };
  },
  computed: {
    dialogOpen: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit("update:open", Boolean(value));
      },
    },
    appliedFilters() {
      return normalizeContactsAdvancedFilters(this.modelValue);
    },
    appliedConditions() {
      return this.appliedFilters.conditions;
    },
    appliedLogic() {
      return this.appliedFilters.logic;
    },
    hasActiveFilters() {
      return this.appliedConditions.length > 0;
    },
    activeConditionCount() {
      return this.appliedConditions.length;
    },
    fieldOptions() {
      return this.fields.map((field) => ({
        key: field.key,
        title: this.fieldTitle(field),
      }));
    },
    privacyStateOptions() {
      return PRIVACY_STATUS_CODES.map((value) => ({
        value, title: this.$t(`ncrm.contacts.dataProtection.states.${value}`),
      }));
    },
    operatorOptions() {
      return CONTACTS_FILTER_OPERATOR_VALUES.map((value) => ({
        value,
        label: this.operatorLabel(value),
      }));
    },
    logicOptions() {
      return [
        { value: "and", label: this.$t("ncrm.contacts.advancedFilter.logicAnd") },
        { value: "or", label: this.$t("ncrm.contacts.advancedFilter.logicOr") },
      ];
    },
    selectMenuProps() {
      return { maxHeight: 320, zIndex: 2710 };
    },
    draftHasValidCondition() {
      return this.draftConditions.some((condition) => this.isConditionComplete(condition));
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.initDraft();
        this.ensureFieldsLoaded();
      }
    },
    type() {
      this.fields = [];
      this.fieldsLoadedForType = "";
    },
  },
  beforeUnmount() {
    this.draftConditions.forEach((condition) => {
      if (condition.search_debounce_id) {
        clearTimeout(condition.search_debounce_id);
      }
    });
  },
  methods: {
    fieldTitle(field) {
      if (field?.key === "privacy_state") return this.$t("ncrm.contacts.dataProtection.header");
      const label = String(field?.label || "");
      if (label.includes("•")) return label;
      return `${field?.group_label || ""} • ${label}`.replace(/^ • /, "");
    },
    operatorLabel(value) {
      const map = {
        equals: "ncrm.contacts.advancedFilter.operators.equals",
        not_equals: "ncrm.contacts.advancedFilter.operators.notEquals",
        contains: "ncrm.contacts.advancedFilter.operators.contains",
        not_contains: "ncrm.contacts.advancedFilter.operators.notContains",
        is_empty: "ncrm.contacts.advancedFilter.operators.isEmpty",
        is_not_empty: "ncrm.contacts.advancedFilter.operators.isNotEmpty",
      };
      return map[value] ? this.$t(map[value]) : value;
    },
    operatorRequiresValue(operator) {
      return contactsFilterOperatorRequiresValue(operator);
    },
    operatorOptionsFor(condition) {
      const allowed = this.fieldMetaForKey(condition?.field_key)?.operators;
      return Array.isArray(allowed)
        ? this.operatorOptions.filter((item) => allowed.includes(item.value))
        : this.operatorOptions;
    },
    valueOptionsFor(condition) {
      if (condition?.field_key === "privacy_state") return this.privacyStateOptions;
      return (condition?.value_suggestions || []).map((value) => ({ value, title: value }));
    },
    isConditionComplete(condition) {
      if (!condition?.field_key) return false;
      if (!this.operatorRequiresValue(condition.operator)) return true;
      return Boolean(String(condition.value ?? "").trim());
    },
    createConditionRow(source = {}) {
      return {
        _uid: nextConditionUid(),
        field_key: source.field_key || "",
        operator: source.operator || "equals",
        value: source.value || "",
        value_suggestions: [],
        value_loading: false,
        search_debounce_id: null,
        search_request_seq: 0,
      };
    },
    openBuilder() {
      this.dialogOpen = true;
    },
    initDraft() {
      this.draftLogic = this.appliedLogic;
      this.draftConditions = this.appliedConditions.length
        ? this.appliedConditions.map((condition) => this.createConditionRow(condition))
        : [this.createConditionRow()];
    },
    addConditionRow() {
      this.draftConditions.push(this.createConditionRow());
    },
    removeConditionRow(index) {
      if (this.draftConditions.length <= 1) return;
      const [removed] = this.draftConditions.splice(index, 1);
      if (removed?.search_debounce_id) {
        clearTimeout(removed.search_debounce_id);
      }
    },
    fieldMetaForKey(fieldKey) {
      return this.fields.find((field) => String(field.key) === String(fieldKey)) || null;
    },
    onConditionFieldChanged(condition) {
      condition.value = "";
      condition.value_suggestions = [];
      condition.value_loading = false;
      const allowed = this.operatorOptionsFor(condition);
      if (!allowed.some((item) => item.value === condition.operator)) {
        condition.operator = allowed[0]?.value || "equals";
      }
    },
    onConditionOperatorChanged(condition) {
      if (!this.operatorRequiresValue(condition.operator)) {
        condition.value = "";
        condition.value_suggestions = [];
      }
    },
    async ensureFieldsLoaded() {
      if (this.fieldsLoadedForType === this.type && this.fields.length) return;
      this.fieldsLoading = true;
      this.fieldsError = "";
      try {
        const { data } = await axios.get("/ncrm/contacts/filter-fields", {
          params: { type: this.type },
        });
        this.fields = Array.isArray(data?.items) ? data.items : [];
        this.fieldsLoadedForType = this.type;
      } catch (error) {
        this.fields = [];
        this.fieldsError =
          error?.response?.data?.error || this.$t("ncrm.contacts.advancedFilter.loadFieldsError");
      } finally {
        this.fieldsLoading = false;
      }
    },
    ensureValueSuggestions(condition) {
      if (!condition.field_key || condition.field_key === "privacy_state") return;
      if (condition.value_suggestions.length) return;
      this.fetchValueSuggestions(condition, "");
    },
    scheduleValueSearch(condition, search) {
      if (!condition.field_key || condition.field_key === "privacy_state") return;
      if (condition.search_debounce_id) {
        clearTimeout(condition.search_debounce_id);
      }
      condition.search_debounce_id = setTimeout(() => {
        condition.search_debounce_id = null;
        this.fetchValueSuggestions(condition, search);
      }, VALUE_SEARCH_DEBOUNCE_MS);
    },
    async fetchValueSuggestions(condition, search) {
      const requestSeq = (condition.search_request_seq += 1);
      condition.value_loading = true;
      try {
        const { data } = await axios.get("/ncrm/contacts/filter-values", {
          params: {
            type: this.type,
            field_key: condition.field_key,
            operator: this.operatorRequiresValue(condition.operator) ? condition.operator : "equals",
            search: String(search || "").trim() || undefined,
            limit: 20,
          },
        });
        if (requestSeq !== condition.search_request_seq) return;
        condition.value_suggestions = (Array.isArray(data?.items) ? data.items : [])
          .map((item) => String(item ?? "").trim())
          .filter(Boolean);
      } catch (_) {
        if (requestSeq !== condition.search_request_seq) return;
        condition.value_suggestions = [];
      } finally {
        if (requestSeq === condition.search_request_seq) {
          condition.value_loading = false;
        }
      }
    },
    emitFilters(logic, conditions) {
      this.$emit("update:modelValue", normalizeContactsAdvancedFilters({ logic, conditions }));
    },
    applyDraft() {
      const conditions = this.draftConditions
        .filter((condition) => this.isConditionComplete(condition))
        .map((condition) => {
          const meta = this.fieldMetaForKey(condition.field_key);
          return {
            field_key: condition.field_key,
            field_label: meta ? this.fieldTitle(meta) : condition.field_key,
            field_group: meta?.group || "",
            operator: condition.operator,
            value: condition.value,
          };
        });
      this.emitFilters(this.draftLogic, conditions);
      this.dialogOpen = false;
    },
    clearAllFromBuilder() {
      this.emitFilters("and", []);
      this.dialogOpen = false;
    },
  },
};
</script>

<style scoped src="./NcrmContactsAdvancedFilter.styles.css"></style>
